import CountBubble from "@components/CountBubble"
import CountdownTimer from "@components/CountdownTimer"
import { Cart, Chevron, Menu, Search } from "@components/icons"
import Logo from "@components/Logo"
import { PromoProps } from "@components/PromoBanner/common"
import MobilePromoBanner from "@components/PromoBanner/MobilePromoBanner"
import animation, { cubicBezier } from "@constants/animation"
import breakpoints from "@constants/breakpoints"
import colors from "@constants/colors"
import { smallcapsBold13Styles } from "@constants/typography"
import useScrollHandler from "@hooks/useScrollHandler"
import { getIn } from "@utils/index"
import {
  getShopURL,
  getShopURLForBrandDeviceType,
  getShopURLForDevice,
} from "@utils/urls"
import { Link } from "gatsby"
import * as R from "ramda"
import React from "react"
import styled from "styled-components"

const Container = styled.div`
  position: fixed;
  top: 0;
  width: 100%;
  background-color: ${colors.offWhite};
  z-index: 1000;
`

const CountdownTimerWrapper = styled.div`
  z-index: 1002;
  position: fixed;
  top: 0;
  transition: height ${animation.quick} ${cubicBezier};
  width: 100%;
  height: 56px;
`

const PromoBannerWrapper = styled.div<{
  $collapse: boolean
  $hasCountdownTimer?: boolean
}>`
  overflow: hidden;
  transition: height ${animation.quick} ${cubicBezier};
  height: ${({ $collapse }) => ($collapse ? 0 : 49)}px;
`

const HeaderWrapper = styled.div`
  display: flex;
  min-height: 56px;
  padding: 0 0 0 20px;
  align-items: center;
  border-bottom: solid 1px ${colors.dividerLineGray};

  @media ${breakpoints.tablet} {
    min-height: 72px;
    padding: 0 12px 0 32px;
  }
`

const showFixedHeaderProps = `
  opacity: 1;
  transform: translateY(0) translateZ(0);
  transition: opacity ${animation.medium} ${cubicBezier},
    transform ${animation.medium} ${cubicBezier};
  z-index: 1;
`
const showFixedHeaderWithCountdownTimerProps = `
  opacity: 1;
  transform: translateY(56px) translateZ(0);
  transition: opacity ${animation.medium} ${cubicBezier},
    transform ${animation.medium} ${cubicBezier};
  z-index: 1;
`
const FixedHeader = styled.div<{
  $hasCountdownTimer?: boolean
  $show?: boolean
}>`
  display: flex;
  position: absolute;
  top: 56px;
  left: 0;
  right: 0;
  width: 100%;
  height: 49px;
  align-items: center;
  justify-content: space-between;
  will-change: opacity, transform;
  opacity: 0;
  transform: translateY(-50%) translateZ(0);
  transition:
    opacity ${animation.medium} ${cubicBezier},
    transform ${animation.quick} ${cubicBezier};
  background-color: ${colors.offWhite};
  border-top: solid 1px ${colors.white};
  border-bottom: solid 1px ${colors.dividerLineGray};
  pointer-events: none;
  z-index: -1;

  @media ${breakpoints.tablet} {
    top: 72px;
  }

  ${({ $hasCountdownTimer, $show }) =>
    $show
      ? $hasCountdownTimer
        ? showFixedHeaderWithCountdownTimerProps
        : showFixedHeaderProps
      : ""}
`

const Content = styled.div`
  display: flex;
  height: 100%;
  flex-direction: row;
  flex-grow: 1;
  align-items: center;
  justify-content: flex-end;
`

const Button = styled.button`
  display: flex;
  width: 56px;
  height: 100%;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  cursor: pointer;

  &:focus {
    outline: none;
  }
`

const SearchButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  cursor: pointer;

  &:focus {
    outline: none;
  }
`

const CartButton = styled(Button)`
  position: relative;
  margin-left: 8px;

  @media ${breakpoints.tablet} {
    margin-left: 35px;
  }
`

const MenuButton = styled(Button)`
  margin-left: 12px;

  @media ${breakpoints.tablet} {
    margin-left: 42px;
  }
`

const Icon = styled.div`
  width: 24px;
  height: 24px;
`

const BackButton = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  transform: translateX(-10px);

  ${Icon} {
    flex-shrink: 0;
    transform: rotate(180deg);
  }
`

const BackButtonText = styled.div`
  ${smallcapsBold13Styles}
  width: calc(100vw - 250px);
  padding: 3px 0 0;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;

  @media ${breakpoints.tablet} {
    width: calc(100vw - 300px);
  }
`

interface MobileHeaderProps {
  collapsePromoBanner?: boolean
  countdownTimer?: {
    eventEnd?: string
    eventStart: string
    textAfterEvent?: string
    textBeforeEvent: string
    textDuringEvent?: string
  }
  enterpriseLink: string
  expanded?: boolean
  filters?:
    | {}
    | {
        brand?: {
          name: string
          slug: string
        }
        device?: {
          name: string
          slug: string
        }
        deviceType?: {
          menuLabel?: string
          name: string
          slug: string
        }
        series?: {
          name: string
        }
      }
  FixedHeaderComponent?: React.ReactNode
  fixedHeaderTopRef?: any
  onClickCart?: (e) => void
  onClickMenu?: (e) => void
  onClickSearch?: (e) => void
  product?: object
  productCount?: number
  promos?: Array<PromoProps>
  promoScrollDelay: string
  searchValue?: string
  showFilters?: boolean
  showFixedHeader?: boolean
  showPromoBanner?: boolean
  supportLink: string
}

const MobileHeader: React.FC<MobileHeaderProps> = (props) => {
  const filters = props.filters || {}
  const showFilters = !R.isEmpty(props.filters) && props.showFilters
  const deviceType = getIn(filters, "deviceType")
  const device = getIn(filters, "device")
  const brand = getIn(filters, "brand")
  const shopURL = device
    ? getShopURLForDevice(device.slug)
    : brand && deviceType
      ? getShopURLForBrandDeviceType(brand.slug, deviceType.slug)
      : getShopURL()

  const { FixedHeaderComponent, fixedHeaderTopRef, showFixedHeader } = props
  const hasCountdownTimer = !!props.countdownTimer

  const [refPos, setRefPos] = React.useState<any>()
  const handleShowFixedHeader = () => {
    if (fixedHeaderTopRef && fixedHeaderTopRef.current) {
      setRefPos(fixedHeaderTopRef.current.getBoundingClientRect().top)
    }
  }

  if (showFixedHeader && fixedHeaderTopRef) {
    useScrollHandler({ onScroll: handleShowFixedHeader })
  }

  const shouldShowFixedHeader =
    showFixedHeader && fixedHeaderTopRef && refPos
      ? refPos < -60
      : !!showFixedHeader

  return (
    <Container>
      {props.countdownTimer && (
        <CountdownTimerWrapper>
          <CountdownTimer
            eventEnd={props.countdownTimer.eventEnd}
            eventStart={props.countdownTimer.eventStart}
            textAfterEvent={props.countdownTimer.textAfterEvent}
            textBeforeEvent={props.countdownTimer.textBeforeEvent}
            textDuringEvent={props.countdownTimer.textDuringEvent}
          />
        </CountdownTimerWrapper>
      )}
      {props.showPromoBanner && props.promos && (
        <PromoBannerWrapper
          $collapse={!!props.collapsePromoBanner}
          $hasCountdownTimer={hasCountdownTimer}
        >
          <MobilePromoBanner
            promos={props.promos}
            promoScrollDelay={props.promoScrollDelay}
          />
        </PromoBannerWrapper>
      )}
      <HeaderWrapper>
        {showFilters ? (
          <Link to={shopURL}>
            <BackButton>
              <Icon>
                <Chevron />
              </Icon>
              <BackButtonText>{device.name}</BackButtonText>
            </BackButton>
          </Link>
        ) : (
          <Logo />
        )}
        <Content>
          <SearchButton onClick={props.onClickSearch}>
            <Icon>
              <Search />
            </Icon>
          </SearchButton>
          <CartButton aria-label="Cart" onClick={props.onClickCart}>
            <CountBubble count={props.productCount || 0} />
            <Icon>
              <Cart />
            </Icon>
          </CartButton>
          <MenuButton aria-label="Menu" onClick={props.onClickMenu}>
            <Icon>
              <Menu />
            </Icon>
          </MenuButton>
        </Content>
        {showFixedHeader && (
          <FixedHeader
            $hasCountdownTimer={hasCountdownTimer}
            $show={shouldShowFixedHeader}
          >
            {FixedHeaderComponent}
          </FixedHeader>
        )}
      </HeaderWrapper>
    </Container>
  )
}

export default MobileHeader
